<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useCurrency } from 'dashboard/composables/useCurrency';

const props = defineProps({
  companyId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['createDeal']);

const { t, locale } = useI18n();
const store = useStore();
const router = useRouter();
const browserLocale = computed(() => locale.value.replace('_', '-'));
const { currency } = useCurrency();

const isLoading = ref(false);
const deals = ref([]);

const STATUS_CONFIG = {
  open: {
    icon: 'i-lucide-circle-dot',
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    label: 'COMPANY_DETAILS.SIDEBAR.DEALS.STATUS.OPEN',
  },
  won: {
    icon: 'i-lucide-trophy',
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
    label: 'COMPANY_DETAILS.SIDEBAR.DEALS.STATUS.WON',
  },
  lost: {
    icon: 'i-lucide-x-circle',
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    label: 'COMPANY_DETAILS.SIDEBAR.DEALS.STATUS.LOST',
  },
};

const formatCurrency = value => {
  if (!value) return '-';
  return new Intl.NumberFormat(browserLocale.value, {
    style: 'currency',
    currency: currency.value,
  }).format(value);
};

const getStatusConfig = status => STATUS_CONFIG[status] || STATUS_CONFIG.open;

const totalValue = computed(() => {
  return deals.value
    .filter(d => d.status === 'open' || d.status === 'won')
    .reduce((sum, d) => sum + (d.amount || 0), 0);
});

const openDealsCount = computed(
  () => deals.value.filter(d => d.status === 'open').length
);

const wonDealsCount = computed(
  () => deals.value.filter(d => d.status === 'won').length
);

const fetchDeals = async () => {
  isLoading.value = true;
  try {
    const response = await store.dispatch('deals/fetchFiltered', {
      company_id: props.companyId,
    });
    deals.value = response || [];
  } catch (error) {
    deals.value = [];
  } finally {
    isLoading.value = false;
  }
};

const goToDeal = dealId => {
  // Navigate to deal details when we have that page
  // For now, just emit an event
  router.push({ name: 'deals_show', params: { dealId } });
};

const createNewDeal = () => {
  emit('createDeal');
};

onMounted(() => {
  fetchDeals();
});
</script>

<template>
  <div class="flex flex-col h-full px-6">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('COMPANY_DETAILS.SIDEBAR.DEALS.TITLE') }}
      </h3>
      <Button icon="i-lucide-plus" size="xs" @click="createNewDeal" />
    </div>

    <!-- Summary Stats -->
    <div class="grid grid-cols-3 gap-2 mb-4">
      <div class="flex flex-col p-2 rounded-lg bg-n-alpha-2">
        <span class="text-xs text-n-slate-10">
          {{ t('COMPANY_DETAILS.SIDEBAR.DEALS.TOTAL') }}
        </span>
        <span class="text-sm font-medium text-n-slate-12">
          {{ formatCurrency(totalValue) }}
        </span>
      </div>
      <div class="flex flex-col p-2 rounded-lg bg-blue-500/10">
        <span class="text-xs text-blue-600 dark:text-blue-400">
          {{ t('COMPANY_DETAILS.SIDEBAR.DEALS.OPEN') }}
        </span>
        <span class="text-sm font-medium text-blue-700 dark:text-blue-300">
          {{ openDealsCount }}
        </span>
      </div>
      <div class="flex flex-col p-2 rounded-lg bg-emerald-500/10">
        <span class="text-xs text-emerald-600 dark:text-emerald-400">
          {{ t('COMPANY_DETAILS.SIDEBAR.DEALS.WON') }}
        </span>
        <span
          class="text-sm font-medium text-emerald-700 dark:text-emerald-300"
        >
          {{ wonDealsCount }}
        </span>
      </div>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <Spinner />
    </div>

    <div
      v-else-if="deals.length === 0"
      class="flex flex-col items-center justify-center py-8 text-center"
    >
      <Icon icon="i-lucide-briefcase" size="size-8 text-n-slate-9 mb-2" />
      <span class="text-sm text-n-slate-11">
        {{ t('COMPANY_DETAILS.SIDEBAR.DEALS.EMPTY') }}
      </span>
      <Button
        :label="t('COMPANY_DETAILS.SIDEBAR.DEALS.CREATE_FIRST')"
        size="sm"
        class="mt-3"
        @click="createNewDeal"
      />
    </div>

    <div v-else class="flex flex-col gap-2 overflow-y-auto">
      <div
        v-for="deal in deals"
        :key="deal.id"
        class="p-3 transition-colors border rounded-lg cursor-pointer border-n-weak hover:bg-n-alpha-2"
        @click="goToDeal(deal.id)"
      >
        <div class="flex items-start justify-between gap-2">
          <div class="flex flex-col flex-1 min-w-0">
            <span class="text-sm font-medium text-n-slate-12 truncate">
              {{ deal.name }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ deal.stage || t('COMPANY_DETAILS.SIDEBAR.DEALS.NO_STAGE') }}
            </span>
          </div>
          <div
            class="flex items-center gap-1 px-2 py-1 rounded-full text-xs"
            :class="[
              getStatusConfig(deal.status).bgColor,
              getStatusConfig(deal.status).color,
            ]"
          >
            <Icon :icon="getStatusConfig(deal.status).icon" size="size-3" />
            {{ t(getStatusConfig(deal.status).label) }}
          </div>
        </div>
        <div class="flex items-center justify-between mt-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ formatCurrency(deal.amount) }}
          </span>
          <span v-if="deal.probability" class="text-xs text-n-slate-10">
            {{ deal.probability }}%
            {{ t('COMPANY_DETAILS.SIDEBAR.DEALS.PROBABILITY') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
